import React, { useState, useEffect, useRef } from 'react'
import { jt, t } from 'ttag'

import { ReactComponent as AddIcon } from '../../../../assets/v3/icons/add.svg'
import { ReactComponent as StarFilledIcon } from '../../../../assets/v2/icons/star-filled.svg'
import { ReactComponent as StarIcon } from '../../../../assets/v2/icons/star.svg'

import { Button } from '../../../../components/Button/Button'
import YumDialog from '../../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import YumInput from '../../../../components/YumInput/YumInput'
import Checkbox from '@material-ui/core/Checkbox'
import YumCountrySelect from '../../../../components/YumCountrySelect/YumCountrySelect'
import NotificationsWrapper from '../../../../components/Notification/NotificationsWrapper'
import TeamSelectionUI from '../../../../components/TeamSelectionUI/TeamSelectionUI'
import GroupSelectionUI from '../../../../components/GroupSelectionUI/GroupSelectionUI'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'

import {
  JoinCompany$Request,
  UserId,
  GetCompanyDetails$Response,
  CompanyCountriesDto,
  DivisionDto,
  DivisionId,
  CompanyId,
  SetUserCountry$Request,
  SetUserDivision$Request,
  UserCompanyDto,
  UserCompanyId,
  DivisionCategoryRow,
  DivisionCategoryId,
} from '../../../../services/api.types'
import api from '../../../../services/api.services'
import { scrollToTop as scrollToTopFn } from '../../../../common/helpers/scroll'
import {
  isCompanyCountryRequired,
  isCompanyDivisionRequired,
  isCompanyUserCreatedTeams,
  getCompanyMaxTeamSize,
} from '../../../RegisterUser/RegisterUser'
import { red } from '../../../../styles/material-ui-theme-v2'
import { clearNotification } from '../../../../common/notifications/NotificationActions'
import { store } from '../../../../index'
import apiUrl from '../../../../services/apiUrl'
import ApiClient from '../../../../services/ApiClient'
import { INotification } from '../../../../common/notifications/NotificationReducers'
import { yuMuuvPublicCompanyId } from '../../Settings'
import { hasCompanyFeatureFlag } from '../../../../common/helpers/featureFlags'

const scrollToTop = () => {
  setTimeout(() => {
    scrollToTopFn()
  }, 10)
}

export interface IJoinCompanyProps {
  userId: UserId
  showJoinAnother?: boolean
  joinCompanyLoading?: boolean
  userCompanyProfileLoading?: boolean
  countries: CompanyCountriesDto[]
  divisions: DivisionDto[]
  joinedCompanyId: UserCompanyId | null
  userCompanies: UserCompanyDto[]
  createdDivisionId?: DivisionId | null
  notification: INotification | null
  divisionCategories?: DivisionCategoryRow[]
  //companyGroups?: { [categoryId: string]: DivisionDto[] }
}

export interface IJoinCompanyDispatchProps {
  joinCompany(payload: JoinCompany$Request): void
  setUserDivision(payload: SetUserDivision$Request): void
  setUserCountry(payload: SetUserCountry$Request): void
  updateUserCompanyProfile(payload: {
    userId: string
    companyId: CompanyId
    divisionId?: string
    country?: string
  }): void
  setJoinedCompanyId(payload: UserCompanyId | null): void
  setCreatedDivisionId(payload: DivisionId | null): void
  getDivisionCategories(): void
  getDivisions(payload?: any): void
  setUserCategoryDivision(payload: {
    companyId: CompanyId
    userId: UserId
    categoryId: DivisionCategoryId
    divisionId: DivisionId | null
    isAdminView?: boolean
  }): void
}

/**
 * JoinCompany component
 *
 * Provides a dialog for users to join a company by entering a sign-up code
 * After successful join, shows a form for team and country selection for that company
 */
const JoinAnotherCompany: React.FC<IJoinCompanyProps & IJoinCompanyDispatchProps> = ({
  userId,
  showJoinAnother = true,
  joinCompanyLoading = false,
  userCompanyProfileLoading = false,
  joinCompany,
  countries,
  divisions,
  setUserDivision,
  setUserCountry,
  updateUserCompanyProfile,
  setJoinedCompanyId,
  setCreatedDivisionId,
  joinedCompanyId,
  userCompanies,
  createdDivisionId,
  notification,
  divisionCategories,
  //companyGroups,
  getDivisionCategories,
  getDivisions,
  setUserCategoryDivision,
}) => {
  // State
  const [dialogOpen, setDialogOpen] = useState(false)
  const [signUpCode, setSignUpCode] = useState('')
  const [customPolicyUrl, setCustomPolicyUrl] = useState<string | null>(null)
  const [customPolicyTitle, setCustomPolicyTitle] = useState<string | null>(null)
  const [customPolicyConsent, setCustomPolicyConsent] = useState(false)
  const [fetchingCompanyDetails, setFetchingCompanyDetails] = useState(false)
  const [joinLoading, setJoinLoading] = useState(false)
  const [prevJoinLoading, setPrevJoinLoading] = useState(false)
  const [signUpCodeError, setSignUpCodeError] = useState<string | null>(null)
  const [policyError, setPolicyError] = useState<string | null>(null)
  const [companyName, setCompanyName] = useState<string | null>(null)
  //const [companyDivisions, setCompanyDivisions] = useState<DivisionDto[]>([])

  // State for post-join form
  const [showPostJoinForm, setShowPostJoinForm] = useState(false)
  const [selectedDivisionId, setSelectedDivisionId] = useState<DivisionId>('')
  const [selectedCountry, setSelectedCountry] = useState('')
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formErrors, setFormErrors] = useState<{
    division?: string
    country?: string
    group?: { [categoryId: string]: string | undefined }
  }>({})

  // Local state for selected group IDs per category (previously from global state)
  const [selectedGroupIdsPerCategory, setSelectedGroupIdsPerCategory] = useState<{
    [categoryId: string]: DivisionId | null
  }>({})

  // Ref to track whether we can close the dialog
  const canCloseDialogRef = useRef(true)

  // Abort controller for getCompanyDetails request
  const abortControllerRef = useRef<AbortController | null>(null)

  // Track the active sign-up code for which we're fetching details
  const activeSignUpCodeRef = useRef<string>('')

  // Debounce timer reference
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Track which joinedCompanyId we've already initialized for
  const initializedCompanyIdRef = useRef<UserCompanyId | null>(null)

  // Sign-up code input — focused automatically when validation/API surfaces an error.
  const signUpCodeInputRef = useRef<HTMLInputElement | null>(null)

  // Track previous loading state for profile update
  const [prevProfileLoading, setPrevProfileLoading] = useState(false)

  // Track error state separately
  const [hasError, setHasError] = useState(false)

  // State for derived company data - calculated once when companyId changes
  const [companyCountries, setCompanyCountries] = useState<CompanyCountriesDto | null>(null)
  const [companyDivisions, setCompanyDivisions] = useState<DivisionDto[]>([])
  const [companyTeams, setCompanyTeams] = useState<DivisionDto[]>([])
  // Store company-specific division categories in state to avoid re-filtering on every render
  // This keeps category processing centralized and easier to reason about.
  const [companyDivisionCategories, setCompanyDivisionCategories] = useState<DivisionCategoryRow[]>([])
  const [isDivisionRequired, setIsDivisionRequired] = useState(false)
  const [isCountryRequired, setIsCountryRequired] = useState(false)
  const [isUserCreatedTeams, setIsUserCreatedTeams] = useState(false)
  const [maxTeamSize, setMaxTeamSize] = useState<number | undefined>(undefined)

  // Reset error state when the dialog opens or closes
  useEffect(() => {
    if (dialogOpen) {
      setHasError(false)
      setJoinedCompanyId(null)
      setCreatedDivisionId(null)
    }
  }, [dialogOpen])

  // Find the companyId from the userCompanyId
  const getCompanyIdFromUserCompanyId = (userCompanyId: UserCompanyId | null): CompanyId | null => {
    if (!userCompanyId) return null

    // Find the userCompany in the user's companies list that has the matching id
    const userCompany = userCompanies.find((uc) => uc.id === userCompanyId)
    return userCompany ? userCompany.companyId : null
  }

  // Find the companyId from the userCompanyId
  const getUserCompany = (userCompanyId: UserCompanyId | null): UserCompanyDto | null => {
    if (!userCompanyId) return null

    // Find the userCompany in the user's companies list that has the matching id
    const userCompany = userCompanies.find((uc) => uc.id === userCompanyId)
    return userCompany || null
  }

  // Get the correct company ID
  const companyId = getCompanyIdFromUserCompanyId(joinedCompanyId)

  const useAutocompleteSelect = hasCompanyFeatureFlag(userCompanies, companyId, 'useAutocompleteSelect')

  // useEffect(() => {
  //   setJoinedCompanyId(null)
  //   setCreatedDivisionId(null)
  //   console.log('JoinAnotherCompany component mounted')
  // }, [])

  // Initialize form fields only once when a company is joined
  // This runs once per unique joinedCompanyId to populate the form with existing user data
  useEffect(() => {
    // Only run if we have a joinedCompanyId and haven't initialized for it yet
    if (joinedCompanyId) {
      // Try to get company name from user companies list
      const userCompany = getUserCompany(joinedCompanyId)
      if (joinedCompanyId !== initializedCompanyIdRef.current) {
        if (userCompany) {
          setCompanyName(userCompany.companyName)
          setSelectedCountry(userCompany.country)

          // Set team
          setSelectedDivisionId(userCompany.divisionId)

          // Set groups
          const userDivisionIds = userCompany.divisionIds
          if (userDivisionIds && companyDivisionCategories.length > 0) {
            try {
              // Parse the JSON string to get the mapping of categoryId -> divisionId
              const divisionIdsObj = JSON.parse(userDivisionIds)

              // Build the selectedGroupIdsPerCategory object for categories in the current company
              const groupIdsPerCategory: { [categoryId: string]: DivisionId | null } = {}

              // Iterate through all division categories for the current company
              companyDivisionCategories.forEach((category) => {
                // Get the division ID for this category from the parsed object
                groupIdsPerCategory[category.id] = divisionIdsObj[category.id] || null
              })

              // Set the state with all the group IDs per category
              setSelectedGroupIdsPerCategory(groupIdsPerCategory)
            } catch (e) {
              // If parsing fails, log a warning and set empty state
              console.warn('Failed to parse divisionIds:', userDivisionIds)
              setSelectedGroupIdsPerCategory({})
            }
          } else {
            // If no divisionIds or company division categories, set empty state
            setSelectedGroupIdsPerCategory({})
          }

          // Mark this joinedCompanyId as initialized
          initializedCompanyIdRef.current = joinedCompanyId
        }
      } else {
        console.log('Joined company ID already initialized, skipping initialization')
        if (userCompany) {
          // TODO: Set team if newly created by the user
          //if (userCompany.divisionId) {
          const createdDivision = divisions?.find(
            (d) => d.companyId === companyId && d.categoryId === null && d.creatorUserId === userId,
          )
          if (createdDivision) {
            setSelectedDivisionId(createdDivision.id)
          }
          //}
          const userDivisionIds = userCompany.divisionIds
          if (userDivisionIds && companyDivisionCategories.length > 0) {
            try {
              // Parse the JSON string to get the mapping of categoryId -> divisionId
              const userDivisionIdsObj = JSON.parse(userDivisionIds) as { [categoryId: string]: DivisionId }
              //console.log('XXX userDivisionIds:', userDivisionIdsObj)

              // Build the selectedGroupIdsPerCategory object for categories in the current company
              const groupIdsPerCategory = selectedGroupIdsPerCategory

              Object.entries(userDivisionIdsObj).forEach(([key, value]) => {
                const createdDivision = divisions?.find(
                  (d) =>
                    d.companyId === companyId && d.categoryId === key && d.id === value && d.creatorUserId === userId,
                )
                if (createdDivision) {
                  groupIdsPerCategory[createdDivision.categoryId] = value
                }
              })

              // userDivisionIdsObj.forEach((divisionId: DivisionId) => {

              // })

              // Set the state with all the group IDs per category
              setSelectedGroupIdsPerCategory(groupIdsPerCategory)
            } catch (e) {
              // If parsing fails, log a warning and set empty state
              console.warn('Failed to parse userDivisionIds:', userDivisionIds)
            }
          }
        }
      }
    }
    // Reset the ref when joinedCompanyId is cleared (dialog closed/reset)
    else {
      initializedCompanyIdRef.current = null
    }
  }, [joinedCompanyId, userCompanies, divisions, companyDivisionCategories, companyId, userId])

  // Effect to update selectedDivisionId when a new team is created
  // useEffect(() => {
  //   if (companyId && createdDivisionId && showPostJoinForm) {
  //     console.log('New team created, updating selected division ID to:', createdDivisionId)
  //     setSelectedDivisionId(createdDivisionId)

  //     // Clear division error when a new team is created and selected
  //     setFormErrors((prevErrors) => ({
  //       ...prevErrors,
  //       division: undefined,
  //     }))
  //   }
  // }, [createdDivisionId, companyId, showPostJoinForm])

  // Effect to process company data when companyId, countries, or divisions change
  useEffect(() => {
    if (companyId) {
      // Calculate company-specific data once when dependencies change
      const companyCountriesData = (countries || []).find((country) => country.companyId === companyId) || null
      const companyDivisionsData = (divisions || []).filter((division) => division.companyId === companyId)
      const companyTeamsData = (divisions || []).filter(
        (division) => division.companyId === companyId && division.categoryId === null,
      )
      // Filter division categories once per company to avoid repeat filtering in handlers and render
      const companyDivisionCategoriesData = divisionCategories
        ? divisionCategories.filter((category) => category.companyId === companyId)
        : []

      // Update state with calculated values
      setCompanyCountries(companyCountriesData)
      setCompanyDivisions(companyDivisionsData)
      setCompanyTeams(companyTeamsData)
      setCompanyDivisionCategories(companyDivisionCategoriesData)
      setIsDivisionRequired(isCompanyDivisionRequired(companyId, companyDivisionsData))
      setIsCountryRequired(isCompanyCountryRequired(companyId))
      setIsUserCreatedTeams(isCompanyUserCreatedTeams(companyId))
      setMaxTeamSize(getCompanyMaxTeamSize(companyId))
    } else {
      // Reset to default values when no company is selected
      setCompanyCountries(null)
      setCompanyDivisions([])
      setCompanyTeams([])
      setCompanyDivisionCategories([])
      setIsDivisionRequired(false)
      setIsCountryRequired(false)
      setIsUserCreatedTeams(false)
      setMaxTeamSize(undefined)
    }
  }, [companyId, countries, divisions, divisionCategories])

  // Effect to fetch division categories and groups when company is joined
  useEffect(() => {
    if (companyId && showPostJoinForm) {
      console.log('Fetching division categories for all companies')
      getDivisionCategories()
    }
  }, [showPostJoinForm, companyId, getDivisionCategories])

  // Move focus to the sign-up code input whenever an error is raised, so screen
  // reader and keyboard users land on the invalid field without manual navigation.
  useEffect(() => {
    if (signUpCodeError) {
      signUpCodeInputRef.current?.focus()
    }
  }, [signUpCodeError])

  // Effect to fetch groups for each category when categories are loaded
  useEffect(() => {
    if (companyId && showPostJoinForm) {
      getDivisions()
      // getDivisions({
      //   companyId,
      // })
    }
  }, [showPostJoinForm, companyId, getDivisions])

  // Effect to handle successful join
  useEffect(() => {
    // If loading state changes from true to false, it means the operation completed
    if (prevJoinLoading && !joinCompanyLoading) {
      console.log('Join completed. joinedCompanyId (userCompanyId):', joinedCompanyId)
      console.log('Available userCompanies:', userCompanies)
      console.log('Derived companyId:', companyId)

      // Check if we need to show the post-join form
      if (joinedCompanyId) {
        // Get company data to check if we have countries or divisions
        const hasCountries =
          companyId && countries?.some((c) => c.companyId === companyId && c.countries && c.countries.length > 0)
        const hasDivisions = companyId && divisions?.some((d) => d.companyId === companyId)

        // Only show post-join form if there are countries or divisions to select
        if (hasCountries || hasDivisions) {
          console.log('Showing post-join form for newly joined company')
          setShowPostJoinForm(true)
        } else {
          console.log('No countries or divisions available, not showing post-join form')
          setShowPostJoinForm(false)
          // Also close the dialog since there's nothing to configure
          setDialogOpen(false)
          resetFormFields()
        }
      } else {
        console.log('No joinedCompanyId available, not showing post-join form')
        setShowPostJoinForm(false)
      }
    }

    setPrevJoinLoading(joinCompanyLoading)
  }, [joinCompanyLoading, prevJoinLoading, joinedCompanyId, companyId, userCompanies, countries, divisions])

  // Effect to track user company profile update loading state
  useEffect(() => {
    console.log('userCompanyProfileLoading changed:', userCompanyProfileLoading)
    console.log('prevProfileLoading:', prevProfileLoading)

    // If loading state changes from true to false, update canCloseDialogRef
    if (prevProfileLoading && !userCompanyProfileLoading) {
      console.log('Profile update completed')

      // We can now allow the dialog to be closed manually
      canCloseDialogRef.current = true

      // Check if there's an error notification in the Redux state
      const hasErrorNotification = notification && notification.type === 'warning'
      const hasSuccessNotification = notification && notification.type === 'success'

      console.log('Notifications status:', { hasErrorNotification, hasSuccessNotification })

      if (hasErrorNotification) {
        console.log('Error notification detected, keeping dialog open')
        // Keep dialog open on error, but allow user to close it manually
        setHasError(true)
      } else {
        // On success notification or no notification, reset error state
        setHasError(false)

        console.log('No error notifications, closing dialog')
        // Close the dialog on success
        setDialogOpen(false)
        setShowPostJoinForm(false)
        resetFormFields()
      }
    } else if (!prevProfileLoading && userCompanyProfileLoading) {
      console.log('Profile update started - preventing dialog closure')
      canCloseDialogRef.current = false
    }

    // Update previous loading state
    setPrevProfileLoading(userCompanyProfileLoading)
  }, [userCompanyProfileLoading, prevProfileLoading, notification])

  // Fetch company details for the given sign-up code
  const fetchCompanyDetails = async (code: string, abortSignal?: AbortSignal) => {
    try {
      console.log('Fetching company details for:', code)
      setFetchingCompanyDetails(true)

      const endpoint = 'getCompanyDetails'
      const response = (await ApiClient.post(
        apiUrl(`/rpc/${endpoint}`),
        {
          signUpCode: code,
        },
        {
          signal: abortSignal,
        },
      )) as GetCompanyDetails$Response

      // Check if this response is for the current sign-up code
      // This prevents race conditions with multiple requests
      if (activeSignUpCodeRef.current === code) {
        console.log('Received valid company details response')
        const data = response as unknown as GetCompanyDetails$Response & { companyId?: CompanyId }

        setCustomPolicyUrl(data.customPolicyUrl || null)
        setCustomPolicyTitle(data.customPolicyTitle || null)

        // Reset consent whenever policy changes
        if (data.customPolicyUrl) {
          setCustomPolicyConsent(false)
        }

        // Clear errors when we get valid company details
        setSignUpCodeError(null)
      } else {
        console.log('Ignoring stale response for:', code)
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.message === 'canceled') {
        console.log('Company details request was aborted')
      } else if (activeSignUpCodeRef.current === code) {
        console.error('Error fetching company details:', error)
      }
    } finally {
      if (activeSignUpCodeRef.current === code) {
        setFetchingCompanyDetails(false)
      }
    }
  }

  // Handlers
  const handleDialogOpen = () => {
    // Clear any notifications when opening the dialog
    store.dispatch({ type: String(clearNotification) })

    canCloseDialogRef.current = true
    setDialogOpen(true)
    //resetFormFields()
  }

  const handleDialogClose = () => {
    if (!joinCompanyLoading && !fetchingCompanyDetails) {
      setDialogOpen(false)
      setShowPostJoinForm(false)
      resetFormFields()
      // Reset joined company ID in global state
      setJoinedCompanyId(null)
      setCreatedDivisionId(null)
      //scrollToTop()
    }

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  // Special handler for post-join form close
  const handlePostJoinFormClose = () => {
    console.log('Attempt to close post-join form')

    // Don't allow closing if an update is in progress
    if (userCompanyProfileLoading) {
      console.log('Update in progress, preventing dialog close')
      canCloseDialogRef.current = false
      return
    }

    // Check for notifications
    const hasErrorNotification = notification && notification.type === 'warning'
    const hasSuccessNotification = notification && notification.type === 'success'

    // Always allow close on success
    if (hasSuccessNotification) {
      console.log('Success notification detected, allowing dialog close')
      canCloseDialogRef.current = true
      setDialogOpen(false)
      resetFormFields()
      return
    }

    // Don't allow closing if there's an error state
    if (hasError || hasErrorNotification) {
      console.log('Error state detected, allowing manual close')
      // We'll allow manual close since the user is explicitly trying to close
      // the dialog even after seeing the error
      canCloseDialogRef.current = true
      setHasError(false)
    }

    // If required fields are empty, show validation errors
    const errors: {
      division?: string
      country?: string
      group: { [categoryId: string]: string }
    } = {
      group: {},
    }

    // Only check division requirement if there are divisions available to select from
    if (isDivisionRequired && companyTeams.length > 0 && !selectedDivisionId) {
      errors.division = t`Team selection is required.`
    }

    // Loop through division categories and check if required groups are selected
    if (companyDivisionCategories.length > 0) {
      companyDivisionCategories.forEach((category) => {
        if (category.required && !selectedGroupIdsPerCategory[category.id]) {
          errors.group[category.id] = t`Group selection is required.`
        }
      })
    }

    // Only check country requirement if there are countries available to select from
    if (isCountryRequired && companyCountries && companyCountries.countries.length > 0 && !selectedCountry) {
      errors.country = t`Country selection is required.`
    }

    // If group error object is empty, remove it
    if (Object.keys(errors.group).length === 0) {
      delete (errors as { group?: { [categoryId: string]: string } }).group
    }

    // If there are validation errors, show them but don't close
    if (Object.keys(errors).length > 0) {
      console.log('Validation failed, preventing dialog close')
      canCloseDialogRef.current = false
      setFormSubmitted(true)
      setFormErrors(errors)
      setHasError(true)
      return
    }

    // Otherwise, allow dialog to close
    console.log('Validation passed, allowing dialog close')
    canCloseDialogRef.current = true
    setDialogOpen(false)
    resetFormFields()
    //scrollToTop()
  }

  // Helper to reset all form fields
  const resetFormFields = () => {
    setTimeout(() => {
      // console.log('RESET FORM FIELDS')

      // Clear any notifications
      store.dispatch({ type: String(clearNotification) })

      setJoinedCompanyId(null)
      setCreatedDivisionId(null)

      setSignUpCode('')
      setCustomPolicyUrl(null)
      setCustomPolicyTitle(null)
      setCustomPolicyConsent(false)
      setSelectedDivisionId('')
      setSelectedCountry('')
      setFormErrors({})
      setFormSubmitted(false)
      setSignUpCodeError(null)
      setPolicyError(null)
      setShowPostJoinForm(false)
      setHasError(false)

      // Reset tracking variables
      activeSignUpCodeRef.current = ''
      initializedCompanyIdRef.current = null
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }

      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
    }, 200)
  }

  const handleSignUpCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = event.target.value
    setSignUpCode(newCode)

    // Clear error when user starts typing
    if (signUpCodeError) {
      setSignUpCodeError(null)
    }

    // Skip if empty
    if (!newCode.trim()) return

    console.log('Starting to process sign-up code:', newCode)

    // Clear any previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Use a debounce to avoid too many requests while typing
    debounceTimerRef.current = setTimeout(() => {
      if (joinLoading) {
        console.log('Join loading, skipping request')
        return
      }

      const trimmedCode = newCode.trim()
      if (trimmedCode.length >= 3) {
        // Store the current sign-up code we're fetching for
        activeSignUpCodeRef.current = trimmedCode

        // If we already have an ongoing request, abort it
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        // Create a new controller for this request
        const controller = new AbortController()
        abortControllerRef.current = controller

        // Fetch company details with the new controller
        fetchCompanyDetails(trimmedCode, controller.signal)
      }
    }, 500)
  }

  const handleCustomPolicyConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomPolicyConsent(event.target.checked)
    // Clear error when user checks the box
    if (policyError) {
      setPolicyError(null)
    }
  }

  const handleJoinCompany = () => {
    setJoinLoading(true)
    // Check for empty sign-up code and show error
    if (!signUpCode.trim()) {
      setSignUpCodeError(t`Sign-up code is required.`)
      setJoinLoading(false)
      return
    }

    if (joinCompanyLoading) {
      setJoinLoading(false)
      return
    }

    // Cancel any pending company details request
    console.log('Join button clicked, aborting any pending request')
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    // Reset loading state
    setFetchingCompanyDetails(false)

    // Clear the active sign-up code to prevent stale responses
    activeSignUpCodeRef.current = ''

    // Validate form fields
    let hasErrors = false

    // Check if policy consent is required but not provided
    if (customPolicyUrl && !customPolicyConsent) {
      setPolicyError(t`You must agree to the policy`)
      hasErrors = true
      setJoinLoading(false)
      return
    }

    // If there are errors, don't proceed
    if (hasErrors) {
      setJoinLoading(false)
      return
    }

    // Clear any errors before submitting
    setSignUpCodeError(null)
    setPolicyError(null)

    // Validate sign-up code with API before joining
    api.auth
      .getCompanyDetails({ signUpCode: signUpCode.trim() })
      .then((response) => {
        const data = response as unknown as GetCompanyDetails$Response & { companyId?: CompanyId; companyName?: string }

        // Save the company name for display in the post-join form
        if (data.companyName) {
          setCompanyName(data.companyName)
        }

        if (data.customPolicyUrl && !customPolicyConsent) {
          setPolicyError(t`You must agree to the policy`)
          hasErrors = true

          setCustomPolicyUrl(data.customPolicyUrl || null)
          setCustomPolicyTitle(data.customPolicyTitle || null)

          // Reset consent whenever policy changes
          if (data.customPolicyUrl) {
            setCustomPolicyConsent(false)
          }

          setJoinLoading(false)
          return
        }

        // If API call succeeds, proceed with join
        joinCompany({
          userId,
          signUpCode: signUpCode.trim(),
        })
      })
      .catch((error) => {
        console.error('Error validating sign-up code:', error)
        // Now we set the error on button click
        setSignUpCodeError(t`Invalid sign-up code. Please check and try again.`)
        // Reset the post-join form state when join fails
        setShowPostJoinForm(false)
        // Reset joined company ID in global state when validation fails
        setJoinedCompanyId(null)
        setCreatedDivisionId(null)
      })
      .finally(() => {
        setJoinLoading(false)
      })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !joinCompanyLoading && !fetchingCompanyDetails) {
      // Check for empty sign-up code first
      if (!signUpCode.trim()) {
        setSignUpCodeError(t`Sign-up code is required.`)
        return
      }

      // Only proceed if no custom policy or consent is given
      if (!customPolicyUrl || customPolicyConsent) {
        handleJoinCompany()
      } else if (customPolicyUrl) {
        setPolicyError(t`You must agree to the policy`)
      }
    }
  }

  const handleOpenCustomPolicy = (e: React.MouseEvent) => {
    e.preventDefault()
    if (customPolicyUrl) {
      window.open(customPolicyUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const handleDivisionChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: DivisionId }
    setSelectedDivisionId(value)

    // Clear the division error when user selects a value
    if (value) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        division: undefined,
      }))
    }
  }

  const handleCountryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: string }
    setSelectedCountry(value)

    // Clear the country error when user selects a value
    if (value) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        country: undefined,
      }))
    }
  }

  const handleCategoryGroupChange = (categoryId: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: string }
    setSelectedGroupIdsPerCategory((prev) => ({
      ...prev,
      [categoryId]: (value as string) || null,
    }))

    // Clear the division error when user selects a value
    if (value) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        group: { ...prevErrors.group, [categoryId]: undefined },
      }))
    }
  }

  const handleSaveTeamAndCountry = (e?: React.FormEvent) => {
    // Prevent default form submission behavior
    if (e) e.preventDefault()

    console.log('handleSaveTeamAndCountry called')
    setFormSubmitted(true)

    // Reset error state before submitting
    setHasError(false)

    // Clear any existing notifications when attempting a new save
    store.dispatch({ type: String(clearNotification) })

    // If required fields are empty, show validation errors
    const errors: {
      division?: string
      country?: string
      group: { [categoryId: string]: string }
    } = {
      group: {},
    }

    // Only check division requirement if there are divisions available to select from
    if (isDivisionRequired && companyTeams.length > 0 && !selectedDivisionId) {
      errors.division = t`Team selection is required.`
    }

    // Loop through division categories and check if required groups are selected
    if (companyDivisionCategories.length > 0) {
      companyDivisionCategories.forEach((category) => {
        if (category.required && !selectedGroupIdsPerCategory[category.id]) {
          errors.group[category.id] = t`Group selection is required.`
        }
      })
    }

    // Only check country requirement if there are countries available to select from
    if (isCountryRequired && companyCountries && companyCountries.countries.length > 0 && !selectedCountry) {
      errors.country = t`Country selection is required.`
    }

    // Update errors state
    setFormErrors(errors)
    console.log('Form errors:', errors)

    // If group error object is empty, remove it
    if (Object.keys(errors.group).length === 0) {
      delete (errors as { group?: { [categoryId: string]: string } }).group
    }

    // If there are errors, don't proceed
    if (Object.keys(errors).length > 0) {
      console.log('Validation failed, not closing dialog')
      canCloseDialogRef.current = false
      setHasError(true)
      return
    }

    // Check if there's anything to update
    if (!selectedDivisionId && !selectedCountry) {
      console.log('No data entered, closing dialog without updates')
      setDialogOpen(false)
      setShowPostJoinForm(false)
      resetFormFields()
      return
    }

    console.log('Validation passed, proceeding with save')
    if (companyId) {
      // Prevent dialog from closing during save operation
      canCloseDialogRef.current = false

      // Update division and country first
      updateUserCompanyProfile({
        userId,
        companyId,
        divisionId: selectedDivisionId || undefined,
        country: selectedCountry || undefined,
      })

      // Only handle categories from the current company via derived state.
      // Then update group selections for each category
      if (companyDivisionCategories.length > 0) {
        companyDivisionCategories.forEach((category) => {
          const selectedGroupId = selectedGroupIdsPerCategory[category.id] || null
          /*console.log(
            `Setting category division for category ${category.id} (${category.customName || category.type}):`,
            selectedGroupId,
          )
          console.log('About to dispatch setUserCategoryDivision action with:', {
            companyId,
            userId,
            categoryId: category.id,
            divisionId: selectedGroupId,
          })*/
          setUserCategoryDivision({
            companyId,
            userId,
            categoryId: category.id,
            divisionId: selectedGroupId,
            isAdminView: false,
          })
          //console.log('setUserCategoryDivision action dispatched')
        })
      }

      // Don't close the dialog immediately - the useEffect will handle that
      // based on the userCompanyProfileLoading state changes
      //console.log('Save triggered, waiting for loading state to change')
    }
  }

  const cp = (
    <a key={'cp'} className={'link-blue'} onClick={handleOpenCustomPolicy}>
      {customPolicyTitle || t`My organization's policy`}
    </a>
  )

  // Company data is now processed in useEffect and stored in state variables
  // This prevents unnecessary recalculations on every render

  return (
    <>
      {showJoinAnother && (
        <Button
          text={t`Join another`}
          link={true}
          onClick={handleDialogOpen}
          iconLeft={AddIcon}
          disabled={joinCompanyLoading}
        />
      )}

      <YumDialog
        open={dialogOpen}
        className="join-another-company-dialog"
        disableEscapeKeyDown={showPostJoinForm || userCompanyProfileLoading}
        onClose={(event, reason) => {
          // console.log('Dialog onClose triggered with reason:', reason)

          if (reason === 'backdropClick') {
            return
          }

          // Don't close if update is in progress
          if (userCompanyProfileLoading) {
            console.log('Preventing dialog close due to in-progress update')
            canCloseDialogRef.current = false
            return
          }

          // Check for notifications
          const hasErrorNotification = notification && notification.type === 'warning'
          const hasSuccessNotification = notification && notification.type === 'success'

          // Always allow close on success
          if (hasSuccessNotification) {
            console.log('Success notification detected, allowing dialog close')
            handleDialogClose()
            return
          }

          // Don't close if we have an error notification
          if (hasErrorNotification && showPostJoinForm) {
            console.log('Preventing dialog close due to error notification')
            return
          }

          // Don't close if we have an error state
          if (hasError && showPostJoinForm) {
            console.log('Preventing dialog close due to error state')
            return
          }

          // Check if we can close the dialog
          if (showPostJoinForm && !canCloseDialogRef.current) {
            console.log('Preventing dialog close due to validation failures')
            // Run validation again to show errors
            handlePostJoinFormClose()
            return
          }

          // Use appropriate handler based on current form
          if (showPostJoinForm) {
            handlePostJoinFormClose()
          } else {
            handleDialogClose()
          }
        }}
      >
        {!showPostJoinForm ? (
          // Join Company Form
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <YumDialogTitle
              handleClose={joinCompanyLoading || joinLoading ? undefined : handleDialogClose}
            >{t`Join another`}</YumDialogTitle>
            <YumDialogContent>
              <NotificationsWrapper />
              <div>
                <YumInput
                  startIcon={StarFilledIcon}
                  startIconInactive={StarIcon}
                  autoFocus
                  id="signUpCode"
                  label={t`Sign-up code`}
                  fullWidth
                  value={signUpCode}
                  onChange={handleSignUpCodeChange}
                  onKeyDown={handleKeyDown}
                  style={{ marginBottom: signUpCodeError ? 4 : 0 }}
                  disabled={joinCompanyLoading}
                  error={!!signUpCodeError}
                  autoCapitalize="characters"
                  className="uppercase-value"
                  inputRef={signUpCodeInputRef}
                  inputProps={{
                    // Sign-up code is mandatory; announce it as required (WCAG 3.3.2).
                    'aria-required': 'true',
                    'aria-invalid': !!signUpCodeError,
                    'aria-describedby': signUpCodeError ? 'sign-up-code-error' : undefined,
                  }}
                />
                {signUpCodeError && <ErrorMessage id="sign-up-code-error">{signUpCodeError}</ErrorMessage>}
              </div>
              {customPolicyUrl ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    <Checkbox
                      id="customPolicyConsent"
                      checked={customPolicyConsent}
                      onChange={handleCustomPolicyConsentChange}
                      color="primary"
                      disabled={joinCompanyLoading || fetchingCompanyDetails}
                    />
                    <label htmlFor="customPolicyConsent">{jt`I agree to: ${cp}`}</label>
                  </div>
                  {policyError && (
                    <div style={{ marginTop: '-8px', marginLeft: '42px' }}>
                      <ErrorMessage>{policyError}</ErrorMessage>
                    </div>
                  )}
                </>
              ) : null}
            </YumDialogContent>
            <YumDialogActions>
              <YumCancelButton onClick={handleDialogClose} disabled={joinCompanyLoading || joinLoading} />
              <YumSubmitButton
                //text={joinCompanyLoading ? t`Joining...` : fetchingCompanyDetails ? t`Loading...` : t`Join`}
                text={t`Join`}
                onClick={handleJoinCompany}
                //disabled={!signUpCode.trim() || joinCompanyLoading}
                //submitInProgress={joinCompanyLoading || fetchingCompanyDetails}
                disabled={joinCompanyLoading || joinLoading}
                submitInProgress={joinCompanyLoading || joinLoading}
                immersiveLoader={true}
              />
            </YumDialogActions>
          </form>
        ) : (
          // Team & Country Selection Form
          <form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault()
              handleSaveTeamAndCountry(e)
              return false
            }}
          >
            <YumDialogTitle>{t`Edit profile`}</YumDialogTitle>
            <YumDialogContent>
              <NotificationsWrapper />
              <div style={{ marginBottom: '16px' }}>
                {t`Successfully joined`}: <strong>{companyName}</strong>
              </div>
              <div style={{ marginBottom: '16px' }}>
                {companyTeams.length > 0 && (
                  <>
                    <TeamSelectionUI
                      companyId={companyId || ''}
                      userId={userId}
                      divisionId={selectedDivisionId}
                      divisions={companyTeams}
                      teamSelectionLocked={false}
                      useAutocompleteSelect={useAutocompleteSelect}
                      maxTeamSize={maxTeamSize}
                      onDivisionChange={(companyId, userId) => (event) => {
                        handleDivisionChange(event)
                      }}
                      error={!!(formSubmitted && formErrors.division)}
                      errorMessage={formErrors.division}
                      disabled={userCompanyProfileLoading}
                      showIcon={true}
                    />
                    {/*{formSubmitted && formErrors.division && <ErrorMessage>{formErrors.division}</ErrorMessage>}*/}
                  </>
                )}
              </div>

              {/* Division Category Groups - Show individual select boxes for each category */}
              {companyDivisionCategories.length > 0 &&
                companyDivisions &&
                selectedGroupIdsPerCategory &&
                companyDivisionCategories.map((category) => {
                  const categoryGroups =
                    companyDivisions.filter((division) => division.categoryId === category.id) || []
                  return (
                    <div key={category.id} style={{ marginBottom: '16px' }}>
                      <GroupSelectionUI
                        companyId={companyId || ''}
                        userId={userId}
                        category={category}
                        groups={categoryGroups}
                        selectedGroupId={selectedGroupIdsPerCategory[category.id] || null}
                        onGroupChange={handleCategoryGroupChange}
                        showIcon={true}
                        useAutocompleteSelect={useAutocompleteSelect}
                        isDuringRegistration={false}
                        error={!!(formSubmitted && formErrors.group?.[category.id])}
                        errorMessage={formErrors.group?.[category.id]}
                      />
                    </div>
                  )
                })}
              <div style={{ marginBottom: '16px' }}>
                {companyCountries && companyCountries.countries.length > 0 && (
                  <YumCountrySelect
                    label={t`Country`}
                    countries={companyCountries.countries}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    required={isCountryRequired}
                    showIcon={true}
                    useAutocompleteSelect={useAutocompleteSelect}
                    style={{ marginBottom: 0 }}
                    error={formSubmitted && isCountryRequired && !selectedCountry}
                    disabled={userCompanyProfileLoading}
                  />
                )}
                {formSubmitted && formErrors.country && <ErrorMessage>{formErrors.country}</ErrorMessage>}
              </div>
            </YumDialogContent>
            <YumDialogActions style={{ paddingTop: '8px' }}>
              <YumSubmitButton
                text={t`Save`}
                onClick={handleSaveTeamAndCountry}
                type="button"
                disabled={userCompanyProfileLoading}
                submitInProgress={userCompanyProfileLoading}
                immersiveLoader={true}
              />
            </YumDialogActions>
          </form>
        )}
      </YumDialog>
    </>
  )
}

export default JoinAnotherCompany
